import { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import "./reportStatistics.css";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faClipboardList,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";

const tabLabels = [
  { name: "All Report Statistics", icon: faChartBar },
  { name: "By Submission Stats", icon: faClipboardList },
  { name: "By Student Attendance", icon: faUserCheck },
];

const barColors = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#6366f1",
  "#14b8a6",
];

const ReportStatistics = () => {
  const API_ENDPOINT = process.env.REACT_APP_API_END_POINT;
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalReports, setTotalReports] = useState(0);
  const [missedReports, setMissedReports] = useState(0);
  const [unreviewedReports, setUnreviewedReports] = useState(0);
  const [reviewedReports, setReviewedReports] = useState(0);
  const [userId, setUserId] = useState("");
  const [submissionStartDate, setSubmissionStartDate] = useState("");
  const [submissionEndDate, setSubmissionEndDate] = useState("");
  const [activeTab, setActiveTab] = useState("All Report Statistics");
  const [groupId, setGroupId] = useState("");
  const [attendanceStartDate, setAttendanceStartDate] = useState("");
  const [attendanceEndDate, setAttendanceEndDate] = useState("");
  const [attendanceStats, setAttendanceStats] = useState(null);
  const submissionRef = useRef(null);
  const [attendanceView, setAttendanceView] = useState("");
  const [moduleId, setModuleId] = useState("");

  // Custom Range dropdown data
  const [customRangeGroups, setCustomRangeGroups] = useState([]);
  const [customRangeModules, setCustomRangeModules] = useState([]);
  const [customRangeLecturers, setCustomRangeLecturers] = useState([]);

  // All Time dropdown data
  const [allTimeGroups, setAllTimeGroups] = useState([]);
  const [allTimeModules, setAllTimeModules] = useState([]);
  const [allTimeLecturers, setAllTimeLecturers] = useState([]);
  const [pieData, setPieData] = useState([]);
  const barColors1 = [
    "#4caf50", // green
    "#f44336", // red
    "#2196f3", // blue
    "#ff9800", // orange
    "#9c27b0", // purple
    "#00bcd4", // cyan
    "#607d8b", // grey-blue
    "#795548", // brown
  ];
  const LecturerSelect = ({ onSelect }) => {
    const [lecturers, setLecturers] = useState([]);
    const [selectedLecturerId, setSelectedLecturerId] = useState("");

    useEffect(() => {
      const fetchReviewerDepartment = async () => {
        try {
          const response = await axios.get(
            `${API_ENDPOINT}/api/UserGetters/GetAllReviewers`,
            {
              withCredentials: true,
              headers: { "Content-Type": "application/json" },
            }
          );
          if (response?.data?.status) {
            const reviewerDepartment = response.data.reviewerDepartment;

            fetchDepartmentLecturers(reviewerDepartment);
          }
        } catch (err) {
          
        }
      };

      const fetchDepartmentLecturers = async (department) => {
        try {
          const response = await axios.get(
            `${API_ENDPOINT}/api/UserGetters/GetDepartmentLecturers?department=${department}`,
            {
              withCredentials: true,
              headers: { "Content-Type": "application/json" },
            }
          );
          if (response?.data?.status) {
            setLecturers(response.data.lecturers);
          }
        } catch (err) {
         
        }
      };

      fetchReviewerDepartment();
    }, []);

    const handleSelectChange = (e) => {
      const selectedId = e.target.value;
      setSelectedLecturerId(selectedId);
      onSelect(selectedId);
    };

    return (
      <div className="custom-dropdown">
        <span className="status-dot green"></span>
        <select value={selectedLecturerId} onChange={handleSelectChange}>
          <option value="">Select A Lecturer</option>
          {lecturers.map((lect) => (
            <option key={lect.userId} value={lect.userId}>
              {lect.lecturerName} {lect.lecturerSurname}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const fetchWithToast = async (url, setter, label, valueKey = "data") => {
    try {
      const resp = await axios.get(url, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      const d = resp.data;
      if (d.status) setter(d[valueKey] ?? 0);
      else toast.error();
    } catch (err) {
      
    }
  };

  const fetchSubmissionRate = async () => {
    if (!userId || !submissionStartDate || !submissionEndDate) {
      Swal.fire({
  icon: "error",
  title: "Error",
  text: "Please Choose Name, Surname, Start Date, and End Date Before Clicking Fetch.",
});
      return;
    }
    try {
      const resp = await axios.get(
        `${API_ENDPOINT}/api/stats/reviewer/GetLecturerSubmissionRate`,
        {
          params: {
            userid: userId,
            startDate: submissionStartDate,
            endDate: submissionEndDate,
          },
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );
      const res = resp.data;
      if (res.status) {
       // Create pie chart data from new fields
      const pieChartData = [
        { name: "Submission Rate", value: parseFloat(res.submissionRate || 0) },
        { name: "Missed Submission Rate", value: parseFloat(res.missedSubmissionRate || 0) },
        
        { name: "Early Submission Rate", value: parseFloat(res.earlySubmissionRate || 0) },
        
        { name: "Late Submission Rate", value: parseFloat(res.lateSubmissionRate || 0) },
        { name: "Total Reports", value: parseInt(res.totalReports || 0) },
        
      ];

      setPieData(pieChartData);

        Swal.fire({
        icon: "success",
        title: "Success",
        text: "Submission stats loaded.",
        timer: 2000,
        showConfirmButton: false,
      });
      } else {
        Swal.fire({
      icon: "error",
      title: "Error",
      text: res.message || "Failed to fetch submission rate.",
    });
      }
    } catch (err) {
      
    }
  };

  useEffect(() => {
    const fetchReviewerDepartment = async () => {
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/api/UserGetters/GetAllReviewers`,
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response?.data?.status) {
          const reviewerDepartment = response.data.reviewerDepartment;
          await fetchAllOptionsByDepartment(reviewerDepartment);
        } else {
          toast.error(
           
          );
        }
      } catch (err) {
        
      }
    };

    const fetchAllOptionsByDepartment = async (deptId) => {
      try {
        const config = {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        };

        // Fetch lecturers
        const lecturersResp = await axios.get(
          `${API_ENDPOINT}/api/UserGetters/GetDepartmentLecturers?department=${deptId}`,
          config
        );

        if (
          lecturersResp.data.status &&
          Array.isArray(lecturersResp.data.lecturers)
        ) {
          const lecturers = lecturersResp.data.lecturers.map((lect) => ({
            userId: lect.userId,
            name: lect.lecturerName,
            surname: lect.lecturerSurname,
          }));
          setCustomRangeLecturers(lecturers);
          setAllTimeLecturers(lecturers);
        } else {
          
        }

        // Fetch groups and modules
        const groupsResp = await axios.get(
          `${API_ENDPOINT}/api/UserGetters/GetAllGroupsPerDepartment?department=${deptId}`,
          config
        );

        if (groupsResp.data.status && Array.isArray(groupsResp.data.data)) {
          const rawData = groupsResp.data.data;

          // Deduplicate groups by groupId
          const groupMap = new Map();
          rawData.forEach((g) => {
            if (g.groupId != null) {
              if (!groupMap.has(g.groupId)) {
                groupMap.set(g.groupId, {
                  groupId: g.groupId,
                  groupName: g.groupName,
                });
              }
            }
          });
          const groups = Array.from(groupMap.values());
          setCustomRangeGroups(groups);
          setAllTimeGroups(groups);

          // Deduplicate modules by normalized moduleName
          const moduleMap = new Map();
          rawData.forEach((g) => {
            if (g.moduleName && g.moduleId) {
              const key = g.moduleName.trim().toLowerCase();
              if (!moduleMap.has(key)) {
                moduleMap.set(key, {
                  moduleId: g.moduleId, // Include moduleId here
                  moduleName: g.moduleName.trim(),
                });
              }
            }
          });

          const modules = Array.from(moduleMap.values());
          setCustomRangeModules(modules);
          setAllTimeModules(modules);
        } else {
          toast.error(
           
          );
        }
      } catch (err) {
        
      }
    };

    fetchReviewerDepartment();
  }, [API_ENDPOINT]);

  const fetchAttendanceStats = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      };

      let res;
      let params = {};

      if (attendanceView === "customRange") {
        if (!groupId || !attendanceStartDate || !attendanceEndDate) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Group, start date, and end date are required.",
          });
          return;
        }
        params = {
          groupId,
          startDate: attendanceStartDate,
          endDate: attendanceEndDate,
        };
        res = await axios.get(
          `${API_ENDPOINT}/api/stats/attendance/GetCustomDateRangeAttendance`,
          { params, ...config }
        );
      } else if (attendanceView === "allTime") {
        if (!groupId) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Group is required for all-time view.",
          });
          return;
        }
        params = { groupId };
        res = await axios.get(
          `${API_ENDPOINT}/api/stats/attendance/GetAllTimeAttendance`,
          { params, ...config }
        );
      } else if (attendanceView === "customRangeByModule") {
        if (!moduleId || !attendanceStartDate || !attendanceEndDate) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Module, start date, and end date are required.",
          });
          return;
        }
        params = {
          moduleId,
          startDate: attendanceStartDate,
          endDate: attendanceEndDate,
        };
        res = await axios.get(
          `${API_ENDPOINT}/api/stats/attendance/GetCustomDateRangeAttendanceByModule`,
          { params, ...config }
        );
      } else if (attendanceView === "allTimeByModule") {
        if (!moduleId) {
          Swal.fire({
  icon: "error",
  title: "Error",
  text: "Module is required.",
});
          return;
        }
        params = { moduleId };
        res = await axios.get(
          `${API_ENDPOINT}/api/stats/attendance/GetAllTimeAttendanceByModule`,
          { params, ...config }
        );
      } else if (attendanceView === "customRangeByLecturer") {
        if (!userId || !attendanceStartDate || !attendanceEndDate) {
          Swal.fire({
  icon: "error",
  title: "Error",
  text: "Lecturer, start date, and end date are required.",
});
          return;
        }
        params = {
          userId,
          startDate: attendanceStartDate,
          endDate: attendanceEndDate,
        };
        res = await axios.get(
          `${API_ENDPOINT}/api/stats/attendance/GetCustomDateRangeAttendanceByLecturer`,
          { params, ...config }
        );
      } else if (attendanceView === "allTimeByLecturer") {
        if (!userId) {
         Swal.fire({
    icon: "error",
  title: "Error",
  text: "Lecturer is required.",
});
          return;
        }
        params = { userId };
        res = await axios.get(
          `${API_ENDPOINT}/api/stats/attendance/GetAllTimeAttendanceByLecturer`,
          { params, ...config }
        );
      } else {
        toast.error();
        return;
      }

      if (!res || !res.data) {
        toast.error();
        return;
      }

      const { status, ...data } = res.data;

      if (status) {
        const total = parseInt(data.totalStudents || 0, 10);
        const rawPercentage = parseFloat(data.attendancePercentage || 0);
        const percentage = parseFloat(rawPercentage.toFixed(2));

        // Calculate attended using percentage if not already provided
        let attended = parseInt(data.attendedStudents || 0, 10);
        if ((!attended || attended === 0) && total > 0 && percentage >= 0) {
          attended = Math.round((percentage / 100) * total);
        }

        const absent =
          typeof data.absentStudents === "number"
            ? Math.round(data.absentStudents)
            : Math.max(0, total - attended);

        const pieChartData = [
          { name: "Present", value: attended, color: "#4caf50" },
          { name: "Absent", value: absent, color: "#f44336" },
        ];

        const stats = {
          ...data,
          pieChartData,
          highestAttendance: percentage,
          lowestAttendance: parseFloat((100 - percentage).toFixed(2)),
          averageRate: percentage,
          totalStudents: total,
          attendedStudents: attended,
          absentStudents: absent,
          dateRange: data.period,
        };

        setAttendanceStats(stats);
        Swal.fire({
        icon: "success",
        title: "Success",
        text: "Stats loaded successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
      } else {
       Swal.fire({
  icon: "error",
  title: "Error",
  text: data.message || "Failed to fetch attendance stats.",
});
      }
    } catch (err) {
     
    }
  };

  useEffect(() => {
    const now = new Date().toLocaleString("en-ZA", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    setCurrentDateTime(now);

    const fetchStats = async () => {
      setLoading(true);
      await Promise.all([
        fetchWithToast(
          `${API_ENDPOINT}/api/stats/reviewer/GetTotalReportsCount`,
          setTotalReports,
          "total reports count",
          "count"
        ),
        fetchWithToast(
          `${API_ENDPOINT}/api/stats/reviewer/GetMissedReportsCount`,
          setMissedReports,
          "missed reports count",
          "count"
        ),
        fetchWithToast(
          `${API_ENDPOINT}/api/stats/reviewer/GetUnreviewedReportsCount`,
          setUnreviewedReports,
          "unreviewed reports count",
          "count"
        ),
        fetchWithToast(
          `${API_ENDPOINT}/api/stats/reviewer/GetReviewedReportsCount`,
          setReviewedReports,
          "reviewed reports count",
          "count"
        ),
      ]);
      setData([
        {
          category: "Overall",
          numReports: totalReports,
          reviewedCount: reviewedReports,
          pendingReports: unreviewedReports,
          missedReports,
        },
      ]);
      setLoading(false);
    };

    fetchStats();
  }, [
    totalReports,
    missedReports,
    unreviewedReports,
    reviewedReports,
    API_ENDPOINT,
  ]);

  const renderKeyLegend = (labels, colors) => (
    <div className="report-key">
      <h3>Key:</h3>
      <ul>
        {labels.map((lbl, i) => (
          <li key={lbl}>
            <span
              className="legend-color"
              style={{ backgroundColor: colors[i] }}
            ></span>
            {lbl}
          </li>
        ))}
      </ul>
    </div>
  );

  const renderKPICounters = () => (
    <div className="kpi-counters">
      {[
        "Total Reports",
        "Reviewed Reports",
        "Pending Reports",
        "Missed Reports",
      ].map((lbl, idx) => {
        const val = [
          totalReports,
          reviewedReports,
          unreviewedReports,
          missedReports,
        ][idx];
        return (
          <div
            key={lbl}
            className={`kpi-card ${lbl.toLowerCase().replace(/ /g, "-")}`}
          >
            <h3>{lbl}</h3>
            <p>{val}</p>
          </div>
        );
      })}
    </div>
  );

  const renderCharts = () => {
    if (loading) return <p>Loading stats...</p>;

    return (
      <>
        {activeTab === "All Report Statistics" && (
          <>
            {renderKPICounters()}
            <div style={{ width: "100%", height: "calc(100vh - 120px)" }}>
              {" "}
              {/* Adjust height offset if needed */}
              <div style={{ width: "100%", height: "100%" }}>
                <h3>Bar Chart</h3>
                {renderKeyLegend(["Reports", "Reviewed", "Pending"], barColors)}
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="category"
                      angle={-40}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="numReports" fill={barColors[0]} />
                    <Bar dataKey="reviewedCount" fill={barColors[1]} />
                    <Bar dataKey="pendingReports" fill={barColors[2]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {activeTab === "By Submission Stats" && (
  <div className="section submission-section" ref={submissionRef}>
    <h3>Submission Stats</h3>
    <small style={{ fontSize: "0.8rem", color: "#555" }}>Select A Lecturer</small>
    <div className="submission-inputs">
      <LecturerSelect onSelect={(id) => setUserId(id)} />
      <input
        type="date"
        value={submissionStartDate}
        onChange={(e) => setSubmissionStartDate(e.target.value)}
      />
      <input
        type="date"
        value={submissionEndDate}
        onChange={(e) => setSubmissionEndDate(e.target.value)}
      />
      <button onClick={fetchSubmissionRate}>Fetch</button>
    </div>

    

    <div className="section pie-chart-section">
      <h3>Pie Chart</h3>
      {renderKeyLegend(
        [
          "Submission Rate",
          "Missed Submission Rate",
          "Early Submission Rate",
          "Late Submission Rate",
          "Total Reports"     
        ],
        barColors1
      )}
           
          
          
          
         
       

      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Tooltip />
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={120}
            label
          >
            {pieData.map((_, i) => (
              <Cell key={i} fill={barColors1[i % barColors1.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  </div>
)}
         {activeTab === "By Student Attendance" && (
  <div className="attendance-section">
    <h2 className="section-title">Attendance Statistics</h2>

    {/* View Option Dropdown */}
    <div className="view-dropdown-wrapper">
      <label htmlFor="attendanceView">View Option</label>
      <select
      
        id="attendanceView"
        value={attendanceView}
        onChange={(e) => setAttendanceView(e.target.value)}
        
      >
      
        <option value="" disabled hidden> Choose Filter</option>

        <option value="allTimeByLecturer">All Time by Lecturer</option>
        <option value="customRangeByLecturer">Custom Range by Lecturer</option>
        <option value="allTimeByModule">All Time by Module</option>
        <option value="customRangeByModule">Custom Range by Module</option>
        <option value="allTime">All Time by Group</option>
        <option value="customRange">Custom Range by Group</option>
        
        
      </select>
     
    </div>
    {/* Filters */}
    <div className="filter-bar">
      {/* Lecturer */}
      {["customRangeByLecturer", "allTimeByLecturer"].includes(attendanceView) && (
        <div className="filter-item">
          <label>Lecturer</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">All Lecturers</option>
            {(attendanceView === "customRangeByLecturer"
              ? customRangeLecturers
              : allTimeLecturers
            ).map((lect) => (
              <option key={lect.userId} value={lect.userId}>
                {lect.name} {lect.surname}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* Modules */}
      {["customRangeByModule", "allTimeByModule"].includes(attendanceView) && (
        <div className="filter-item">
          <label>Modules</label>
          <select value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
            <option value="">All Modules</option>
            {(attendanceView === "customRangeByModule"
              ? customRangeModules
              : allTimeModules
            ).map((m, index) => (
              <option key={index} value={m.moduleId}>
                {m.moduleName}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Group */}
      {["customRange", "allTime"].includes(attendanceView) && (
        <div className="filter-item">
          <label>Group</label>
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">All Groups</option>
            {(attendanceView === "customRange"
              ? customRangeGroups
              : allTimeGroups
            ).map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.groupName}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* From Date */}
      {["customRange", "customRangeByModule", "customRangeByLecturer"].includes(
        attendanceView
      ) && (
        <div className="filter-item">
          <label>From Date</label>
          <input
            type="date"
            value={attendanceStartDate}
            onChange={(e) => setAttendanceStartDate(e.target.value)}
          />
        </div>
      )}

      {/* To Date */}
      {["customRange", "customRangeByModule", "customRangeByLecturer"].includes(
        attendanceView
      ) && (
        <div className="filter-item">
          <label>To Date</label>
          <input
            type="date"
            value={attendanceEndDate}
            onChange={(e) => setAttendanceEndDate(e.target.value)}
          />
        </div>
      )}
    </div>
    <div className="view-dropdown-wrapper">

     <button onClick={fetchAttendanceStats}>Fetch Stats</button>
     </div>

    {/* Attendance Breakdown and Quick Stats */}
    {attendanceStats && (
      <div className="stats-grid">
        {/* Pie Chart */}
        <div className="pie-chart-box">
          <h3 className="chart-title">Attendance Breakdown</h3>
          <p className="chart-subtitle">
            {attendanceStats?.moduleCode || attendanceStats?.moduleName || ""}
            {attendanceStats?.groupName && ` Group ${attendanceStats.groupName}`}
            {attendanceStats?.dateRange && ` (${attendanceStats.dateRange})`}
          </p>

          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={attendanceStats.pieChartData || []}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {(attendanceStats.pieChartData || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Legend verticalAlign="bottom" />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>

          <div className="legend-description">
            <span style={{ color: "#4caf50" }}>● Present</span>
            <span style={{ color: "#f44336" }}>● Absent</span>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="quick-stats-box">
          <h3 className="chart-title">Quick Statistics</h3>
          <div className="stat-cards">
            <div className="stat-card green">
              <p className="value">{attendanceStats.highestAttendance}%</p>
              <p className="label">Highest Attendance</p>
            </div>
            <div className="stat-card red">
              <p className="value">{attendanceStats.lowestAttendance}%</p>
              <p className="label">Lowest Attendance</p>
            </div>
            <div className="stat-card blue">
              <p className="value">{attendanceStats.averageRate}%</p>
              <p className="label">Average Rate</p>
            </div>
            <div className="stat-card purple">
              <p className="value">{attendanceStats.totalStudents}</p>
              <p className="label">Total Students</p>
            </div>
            <div className="stat-card green">
              <p className="value">{attendanceStats.attendedStudents}</p>
              <p className="label">Present Students</p>
            </div>
            <div className="stat-card red">
              <p className="value">{attendanceStats.absentStudents}</p>
              <p className="label">Absent Students</p>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
)}
      </>
    );
  };

  return (
    <div className="report-statistics">
      <div className="report-stats-header"></div>
      <h2>Report Statistics</h2>
      <p className="report-stats-datetime">{currentDateTime}</p>
      <div className="tabs">
        {tabLabels.map((tab) => (
          <button
            key={tab.name}
            className={activeTab === tab.name ? "active" : ""}
            onClick={() => setActiveTab(tab.name)}
          >
            <FontAwesomeIcon icon={tab.icon} style={{ marginRight: "8px" }} />
            {tab.name}
          </button>
        ))}
      </div>
      {renderCharts()}
    </div>
  );
};

export default ReportStatistics;
